import { wccModules } from 'enums/wccModules';
import { PositionBindingOptions } from 'extensions/knockout/bindings/position';
import { inject, injectable } from 'inversify';
import { Subscribable, SubscribableOrNullableValue } from 'knockout';
import device from 'managers/device';
import { NodeTracker } from 'managers/nodes/tracker';
import overlayManager from 'managers/overlay';
import { Tag } from 'models/tag';
import { TopicThread } from 'models/topicThread';
import HeatmapVM from '../heatmap';
import './pin.html';
import './pin.scss';

const offset = 15;

const dotWidth = 30;
const dotHeight = 30;

const halfDotWidth = dotWidth / 2;
const halfDotHeight = dotHeight / 2;

const activePin = ko.observable<HeatmapPinVM>();

export type HeatmapPinVMThemes = 'classic' | 'simple';

export interface HeatmapPinVMConfig {
    heatmap?: SubscribableOrNullableValue<HeatmapVM>
    pin?: SubscribableOrNullableValue<TopicThread>
    x?: SubscribableOrNullableValue<number>
    y?: SubscribableOrNullableValue<number>
    tag?: SubscribableOrNullableValue<Tag>
    expand?: SubscribableOrNullableValue<boolean>
    overlay?: SubscribableOrNullableValue<boolean>

    theme?: SubscribableOrNullableValue<HeatmapPinVMThemes>
}

@injectable()
export default class HeatmapPinVM {
    private boxWidth: Subscribable<number>
    private boxHeight: Subscribable<number>

    private heatmapWidth: Subscribable<number | undefined>
    private heatmapHeight: Subscribable<number | undefined>
    private heatmapDx: Subscribable<number | undefined>
    private heatmapDy: Subscribable<number | undefined>

    private x: Subscribable<number | undefined>
    private y: Subscribable<number | undefined>

    private allowOverlay: Subscribable<boolean>

    boxNode = ko.observable<HTMLElement>();

    color: Subscribable<string | undefined>
    position: Subscribable<PositionBindingOptions | undefined>
    maxWidth: Subscribable<number | undefined>

    noBox: Subscribable<boolean>
    showBox: Subscribable<boolean>
    showSmallDot: Subscribable<boolean>
    ready: Subscribable<boolean>

    constructor(
        @inject(wccModules.componentConfig) config: HeatmapPinVMConfig,
        @inject(wccModules.componentInfo) info: ko.components.ComponentInfo
    ) {
        const isExpandedMode = ko.flattenComputed(config.expand, false);
        this.allowOverlay = ko.flattenComputed(config.overlay, true);

        const tracker = new NodeTracker({ node: this.boxNode });
        this.boxWidth = tracker.width;
        this.boxHeight = tracker.height;

        const heatmap = ko.flattenComputed(config.heatmap);
        this.heatmapWidth = heatmap.pluck(h => h.width);
        this.heatmapHeight = heatmap.pluck(h => h.height);
        const heatmapImageWidth = heatmap.pluck(h => h.imageWidth);
        const heatmapImageHeight = heatmap.pluck(h => h.imageHeight);
        this.heatmapDx = heatmap.pluck(h => h.dx);
        this.heatmapDy = heatmap.pluck(h => h.dy);
        this.noBox = heatmap.pluck(h => h.noBox, false);

        const pin = ko.flattenComputed(config.pin);
        const point = pin.pluck(p => p.point);
        const x = point.pluck(p => p.x, config.x);
        const y = point.pluck(p => p.y, config.y);

        this.x = ko.strictComputed((x, heatmapDx, heatmapWidth, heatmapImageWidth) => {
            if (x != undefined && heatmapDx != undefined && heatmapWidth != undefined && heatmapWidth > 0 && heatmapImageWidth != undefined && heatmapImageWidth > 0) {
                const absX = x * heatmapImageWidth;
                const imageCenterX = heatmapImageWidth / 2;
                const centerX = heatmapWidth / 2;
                const viewportX = imageCenterX - centerX - heatmapDx;

                return (absX - viewportX) / heatmapWidth;
            }
        }, [x, this.heatmapDx, this.heatmapWidth, heatmapImageWidth]);

        this.y = ko.strictComputed((y, heatmapDy, heatmapHeight, heatmapImageHeight) => {
            if (y != undefined && heatmapDy != undefined && heatmapHeight != undefined && heatmapHeight > 0 && heatmapImageHeight != undefined && heatmapImageHeight > 0) {
                const absY = y * heatmapImageHeight;
                const imageCenterY = heatmapImageHeight / 2;
                const centerY = heatmapHeight / 2;
                const viewportY = imageCenterY - centerY - heatmapDy;

                return (absY - viewportY) / heatmapHeight;
            }
        }, [y, this.heatmapDy, this.heatmapHeight, heatmapImageHeight]);

        const tags = pin.pluck(p => p.tags);
        const tag = tags.first().default(config.tag);
        this.color = tag.pluck(t => t.tagColor);
        const theme = ko.flattenComputed(config.theme);

        const expanded = activePin.is(this);
        const hovered = ko.observable(false);
        const clickable = ko.pureComputed(() => !isExpandedMode() && !this.noBox());

        this.showSmallDot = ko.pureComputed(() => !expanded() && !hovered() && !isExpandedMode() || this.noBox());
        this.showBox = ko.pureComputed(() => expanded() || isExpandedMode());

        this.position = ko.pureComputed(() => this.getPosition()).extend({ deferred: true });

        this.maxWidth = this.heatmapWidth.pluck(heatmapWidth => heatmapWidth / 2);

        this.ready = ko.strictComputed((x, y) => x != undefined && x > 0 && x < 1 && y != undefined && y > 0 && y < 1, [this.x, this.y]);

        ko.applyBindingsToNode(info.element, {
            'visible': this.ready,

            'css': {
                'wcc-heatmap__pin': true,
                'wcc-heatmap__pin--clickable': clickable,
                'wcc-heatmap__pin--active': this.showBox,
                'wcc-heatmap__pin--simple': theme.is('simple'),
                'wcc-heatmap__pin--mobile': device.isMobile
            },

            'style': {
                'top': this.y.mapNotNull(v => v * 100).toPrts(),
                'left': this.x.mapNotNull(v => v * 100).toPrts(),
                'border-color': this.color
            },

            'event': {
                'mouseenter': () => hovered(true),
                'mouseleave': () => hovered(false)
            }
        });
    }

    onBoxClick = _.noop

    toggle = ($context: ko.BindingContext) => {
        if (activePin() === this) {
            activePin(undefined);
        } else if (!this.noBox()) {
            if (device.isMobile() && this.allowOverlay()) {
                const template = `<div class="wcc-heatmap__pin__mobile-box">
        <!-- ko hideContext: { toContext: context.$parentContext, store: { '$pinContext': context, '$pin': $data } } -->
        <!-- ko template: { nodes: $pinContext.$componentTemplateNodes } --><!-- /ko -->
        <!-- /ko -->
</div>`;

                overlayManager.toOverlay(template, {
                    buttons: ['close'],
                    stretch: true,
                    lock: true,

                    data: {
                        context: $context
                    }
                });
            } else {
                activePin(this);
            }
        }
    }

    private getPosition() {
        if (this.ready() && this.showBox()) {
            const boxWidth = this.boxWidth();
            const boxHeight = this.boxHeight();

            const heatmapWidth = this.heatmapWidth();
            const heatmapHeight = this.heatmapHeight();

            if (heatmapWidth != undefined && heatmapHeight != undefined) {
                const x = this.x();
                const y = this.y();

                if (x != undefined && y != undefined) {
                    const absX = x * heatmapWidth;
                    const absY = y * heatmapHeight;

                    var left = x <= 0.5 ?
                        (absX - offset < halfDotWidth ? halfDotWidth : -offset) :
                        -(absX + offset > heatmapWidth - halfDotWidth ? halfDotWidth : -offset) + dotWidth - boxWidth;

                    var top = y <= 0.5 ?
                        dotHeight + offset :
                        -offset - boxHeight;

                    var bigPinMode = top > 0 && absY + top + boxHeight + halfDotHeight > heatmapHeight ||
                        top < 0 && absY + top - halfDotHeight < 0;

                    if (bigPinMode) {
                        left = x < 0.5 ?
                            dotWidth + offset :
                            -offset - boxWidth;

                        top = -absY + heatmapHeight / 2 - boxHeight / 2 + halfDotHeight;
                    }

                    return <PositionBindingOptions>{
                        left: left,
                        top: top
                    }
                }
            }
        }
    }
}